"use client";

import React from "react";
import { ArrowRight, Hammer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  to: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const services: ServiceItem[] = [{
  id: "kitchens",
  title: "Kitchen Remodeling",
  description: "Chef-grade layouts, handcrafted cabinetry, and bespoke islands crafted for family gatherings.",
  to: "/kitchen-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png-wvc-sizes",
    alt: "Spacious renovated Brookhaven kitchen with custom white shaker cabinets and marble waterfall island"
  }
}, {
  id: "bathrooms",
  title: "Luxury Bathrooms",
  description: "Spa-caliber primary suites featuring curbless walk-in showers, soaking tubs, and custom vanities.",
  to: "/bathroom-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_bathroom.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_bathroom.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_master_bathroom.png-wvc-sizes",
    alt: "Luxury master bathroom suite with freestanding tub and glass walk-in shower enclosure"
  }
}, {
  id: "whole-home",
  title: "Whole-Home Remodeling",
  description: "Comprehensive home reconfigurations that balance structural integrity with modern architectural flow.",
  to: "/whole-home-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_interior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_interior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_interior.png-wvc-sizes",
    alt: "Open-concept Brookhaven residential interior renovation showing living room and dining transition"
  }
}, {
  id: "additions",
  title: "Home Additions",
  description: "Seamless second-story extensions, ground-floor in-law suites, and sunrooms designed to blend flawlessly.",
  to: "/home-additions/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-sizes",
    alt: "Architectural two-story home addition seamlessly integrated with original brick residence"
  }
}, {
  id: "basements",
  title: "Basement Remodeling",
  description: "Turn unused lower levels into custom entertainment lounges, guest quarters, home theaters, and wet bars.",
  to: "/basement-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png-wvc-sizes",
    alt: "Finished walkout basement with recessed lighting, custom wine bar, and comfortable lounge area"
  }
}, {
  id: "interior",
  title: "Interior Remodeling",
  description: "Tailored floor plan alterations, custom millwork, ceiling beams, and contemporary drywall transitions.",
  to: "/interior-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_interior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_interior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_interior.png-wvc-sizes",
    alt: "Custom architectural interior renovation with detailed crown molding, built-in shelving, and hardwood floors"
  }
}, {
  id: "outdoor-living",
  title: "Outdoor Living",
  description: "Year-round covered outdoor patios, louvered pergolas, integrated fire pits, and stone summer kitchens.",
  to: "/outdoor-living/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living_patio.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living_patio.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living_patio.png-wvc-sizes",
    alt: "Covered Brookhaven outdoor living pavilion with stone outdoor kitchen and cedar plank ceiling"
  }
}, {
  id: "decks",
  title: "Deck Remodeling",
  description: "Premium composite and hardwood deck builds engineered for durability, safety, and effortless entertaining.",
  to: "/deck-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_craftsmanship.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_craftsmanship.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_craftsmanship.png-wvc-sizes",
    alt: "Multi-tiered composite deck with dark metal railings overlooking landscaped Georgia backyard"
  }
}];
export default function RemodelingServicesIntro() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="remodelingservicesintro" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 border-b border-border/60 transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Header */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Hammer className="size-3.5 text-primary" />
            <span>Brookhaven, GA Remodeling Services</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
            Full-spectrum residential remodeling built for Brookhaven homes
          </h1>

          <p className="mt-5 text-base sm:text-lg text-foreground/80 leading-relaxed">
            Brookhaven Elevation Renovations delivers disciplined design-build craftsmanship across Fulton and DeKalb County. Whether reimagining historic properties near Capital City Club or elevating contemporary homes throughout Ashford Park and Historic Brookhaven, our master craftsmen handle kitchen, bathroom, whole-home, additions, basement, interior, outdoor living, and custom deck construction with uncompromised precision.
          </p>
        </div>

        {/* Services 8-card Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => <article key={service.id} data-index={index} className="group flex flex-col bg-card text-card-foreground border border-border rounded-lg overflow-hidden shadow-xs hover:shadow-md hover:border-primary/50 transition-all duration-300">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>

              <div className="p-5 flex flex-col flex-1 justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl font-medium text-foreground tracking-tight group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground/75 leading-relaxed">
                    {service.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-foreground hover:bg-transparent hover:text-primary transition-colors">
                    <Link to={service.to}>
                      <span>Explore service</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>)}
        </div>

        {/* Bottom Context Callout */}
        <div className="mt-14 p-6 sm:p-8 rounded-lg bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-sm">
          <div>
            <h4 className="font-serif text-xl font-semibold">
              Ready to start your Brookhaven remodeling project?
            </h4>
            <p className="mt-1 text-sm text-secondary-foreground/80 max-w-2xl">
              From historic renovations to modern expansions, our team coordinates architectural plans, permits, and construction under a single transparent contract.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 font-medium">
            <Link to="/ContactRequestAnEstimatePage">
              Request an estimate
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}